import { ArrowBigLeft } from "lucide-react";
import React, { useState, useRef, useEffect } from "react";

//component for recording voice
const AudioRecorder = ({
  onRecordingComplete,
  setReturn,
  startButtonText = "Start Recording",
  stopButtonText = "Stop Recording",
  showVisualizer = true,
  classes = "",
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setSeconds((prev) => {
          if (prev === 59) {
            setMinutes((m) => m + 1);
            return 0;
          }
          return prev + 1;
        });
      }, 1000);
    } else {
      setMinutes(0);
      setSeconds(0);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  // Handle Start Recording
  const handleStartRecording = () => {
    // Check if getUserMedia is supported
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert("Your browser does not support audio recording.");
      return;
    }

    setIsRecording(true);
    audioChunksRef.current = [];

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        mediaRecorderRef.current = new MediaRecorder(stream);

        mediaRecorderRef.current.ondataavailable = (event) => {
          audioChunksRef.current.push(event.data);
        };

        mediaRecorderRef.current.onstop = () => {
          const audioBlob = new Blob(audioChunksRef.current, {
            type: "audio/wav",
          });
          const audioUrl = URL.createObjectURL(audioBlob);
          setAudioUrl(audioUrl);

          onRecordingComplete && onRecordingComplete(audioBlob, audioUrl);

          stream.getTracks().forEach((track) => track.stop());
        };

        mediaRecorderRef.current.start();
      })
      .catch((error) => {
        console.error("Error accessing audio devices:", error);
        alert(
          "Could not access your microphone. Please check your device and browser permissions."
        );

        setIsRecording(false);
      });
  };

  // Handle Stop Recording
  const handleStopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const handleReturn = () => {
    setReturn(false);
  };

  return (
    <div
      className={`${classes} relative flex flex-col items-center justify-center p-6 sm:p-4 lg:p-1 w-full max-w-md lg:max-w-2xl xl:max-w-5xl lg:flex-row lg:space-x-6 mb-2 mt-1 bg-slate-200 ring-slate-400 ring-1 dark:bg-slate-700 rounded-full`}>
      <div className="flex flex-col items-center mb-4 lg:mb-0 lg:flex-row lg:space-x-3">
        <button
          className={`absolute p-1 rounded-md cursor-pointer left-4 hover:bg-red-500 hover:text-gray-200 dark:text-gray-300 ${
            isRecording &&
            "pointer-events-none bg-slate-300 text-gray-500 dark:bg-slate-800 dark:text-gray-600"
          }`}
          onClick={handleReturn}>
          <ArrowBigLeft size={24} />
        </button>
        <div
          className={`w-14 h-14 rounded-full flex items-center justify-center border-2 ${
            isRecording
              ? "border-red-500  animate-pulse "
              : "bg-slate-300 border-slate-400 dark:bg-slate-600 "
          } transition-all duration-300`}>
          <span className="text-base font-bold text-gray-800 dark:text-gray-100">
            {isRecording
              ? `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`
              : "00:00"}
          </span>
        </div>
        <p className="text-gray-700 text-md dark:text-gray-400 lg:ml-3 ">
          {isRecording ? "Recording..." : "Ready to record"}
        </p>
      </div>

      <button
        onClick={isRecording ? handleStopRecording : handleStartRecording}
        className={`flex items-center justify-center ${
          isRecording
            ? "bg-red-500 hover:bg-red-600"
            : "bg-green-600 dark:bg-green-400 hover:bg-green-700 dark:hover:bg-green-500"
        } dark:text-gray-800 text-gray-200 text-md font-semibold py-1 px-4 rounded-full shadow transition-colors duration-300 transform hover:scale-105`}
        aria-label={isRecording ? "Stop Recording" : "Start Recording"}>
        <span className="">
          {isRecording ? stopButtonText : startButtonText}
        </span>
      </button>

      {audioUrl && (
        <div className="w-full mt-4 lg:mt-0 lg:ml-6">
          <audio
            controls
            className="w-full bg-red-500 rounded-lg">
            <source
              src={audioUrl}
              type="audio/wav"
            />
            Your browser does not support the audio element.
          </audio>
        </div>
      )}
    </div>
  );
};

export default AudioRecorder;
